// src/pages/EcoPickPage.js
import React, { useRef, useEffect, useState, useCallback } from "react";
import AppIntroSection from "../components/AppIntroSection";
import FeatureSection from "../components/FeatureSection";
import "../styles/CompanyIntroPage.css";
import styles from "../styles/EcoPickPage.module.css";

import ecoPickImage from "../assets/data_screen.png";
import col_home from "../assets/col_home.png";
import home from "../assets/home.png";
import alliance_intro from "../assets/alliance_intro.png";
import alliance_screen from "../assets/alliance_screen.png";
import col_data_screen from "../assets/col_data_screen.png";
import col_data_input_screen from "../assets/col_data_input_screen.png";

const appFeatures = [
  {
    title: "💡수거자와 배출자의 자유로운 매칭",
    description:
      "사용자는 배출 현황을, 수거자는 수거량을 실시간으로 확인할 수 있습니다.",
    images: [col_home, home],
  },
  {
    title: "📱 모바일 앱 기반 간편 예약 & 실시간 확인",
    description:
      "사용자는 언제든 수거 품목과 시간을 지정해, 수거 제휴 요청을 할 수 있습니다.",
    images: [alliance_intro, alliance_screen],
  },
  {
    title: "🔄 누구나 수거자로 등록 가능",
    description:
      "수거자는 언제든 수거 위치와 수거량을 확인하고, 수거하여 많은 수익을 창출해낼 수 있습니다.",
    images: [col_data_screen, col_data_input_screen],
  },
];

function EcoPickPage() {
  const [showIntro, setShowIntro] = useState(true);
  const [introFadingOut, setIntroFadingOut] = useState(false); // 페이드 아웃 시작을 알리는 상태

  // Intersection Observer를 위한 refs 및 visibleSections 상태
  const sectionsRef = useRef({}); // 객체 형태로 ref 관리
  const [visibleSections, setVisibleSections] = useState({});

  const setSectionRef = useCallback((node, id) => {
    if (node) {
      sectionsRef.current[id] = node;
    } else {
      delete sectionsRef.current[id]; // 노드가 사라질 때 ref에서도 제거
    }
  }, []);

  const observerCallback = useCallback((entries) => {
    entries.forEach((entry) => {
      setVisibleSections((prev) => ({
        ...prev,
        [entry.target.id]: entry.isIntersecting,
      }));
    });
  }, []);

  useEffect(() => {
    // 인트로 애니메이션 제어
    const introDuration = 2000; // 인트로 보여주는 시간 (2초)
    const fadeOutDuration = 1000; // 페이드 아웃 애니메이션 시간 (1초, CSS transition과 일치)

    const startFadeOutTimer = setTimeout(() => {
      setIntroFadingOut(true); // 페이드 아웃 클래스 추가
    }, introDuration);

    const removeIntroTimer = setTimeout(() => {
      setShowIntro(false); // 인트로 컴포넌트 DOM에서 제거
    }, introDuration + fadeOutDuration); // 보여주는 시간 + 페이드 아웃 시간

    // Intersection Observer 설정
    // 인트로가 사라진 후에만 옵저버를 생성하도록 조건 추가
    let observer;
    if (!showIntro) {
      // showIntro가 false가 된 후에만 Observer 생성
      observer = new IntersectionObserver(observerCallback, {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      });

      // 현재 DOM에 존재하는 섹션들을 관찰
      const currentSections = Object.values(sectionsRef.current);
      currentSections.forEach((section) => {
        if (section) observer.observe(section);
      });
    }

    const cleanupSections = Object.values(sectionsRef.current);
    return () => {
      clearTimeout(startFadeOutTimer);
      clearTimeout(removeIntroTimer);
      if (observer) {
        cleanupSections.forEach((section) => {
          if (section) observer.unobserve(section);
        });
        observer.disconnect(); // 옵저버 연결 해제
      }
    };
  }, [showIntro, observerCallback]); // showIntro가 변경될 때 useEffect 다시 실행

  return (
    <div className="app-one-page">
      {showIntro && (
        <div
          className={`${styles.introContainer} ${
            introFadingOut ? styles.fadeOut : ""
          }`}
        >
          <h1 className={styles.appName}>이코픽</h1>
          <p className={styles.tagline}>
            재활용도 선택이 아닌 연결의 문제입니다.
            <br />
            이코픽은 ‘가치 있는 자원’을 필요한 곳으로 연결합니다.
          </p>
        </div>
      )}

      {!showIntro && ( // showIntro가 false일 때만 이 섹션들을 렌더링
        <>
          <section
            id="app-one-intro"
            ref={(el) => setSectionRef(el, "app-one-intro")}
            className={`section ${
              visibleSections["app-one-intro"] ? "fade-in" : "fade-out"
            }`}
          >
            <AppIntroSection
              appMainScreen={ecoPickImage}
              appName="이코픽"
              appDescription1="상가에서 나오는 재활용 자원을 수거자와 연결해주는 도심형 스마트 수거 매칭 플랫폼입니다."
              appSubDescription2="배출자는 앱으로 간편하게 수거 요청을 등록하고, 수거자는 필요한 장소와 시간에 맞춰 수거를 진행합니다.
  수거 후에는 사진 인증과 함께 자동으로 정산되며, 배출자와 수거자 모두에게 포인트 리워드가 제공됩니다."
            />
          </section>
          {appFeatures.map((feature, index) => (
            <section
              key={index}
              id={`app-one-feature-${index}`}
              ref={(el) => setSectionRef(el, `app-one-feature-${index}`)}
              className={`section ${
                visibleSections[`app-one-feature-${index}`]
                  ? "fade-in"
                  : "fade-out"
              }`}
            >
              <FeatureSection
                title={feature.title}
                description={feature.description}
                images={feature.images}
                isEven={index % 2 === 0}
              />
            </section>
          ))}
        </>
      )}
    </div>
  );
}

export default EcoPickPage;
